import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Table,
  Row,
  Col,
  Input,
  Checkbox,
  Space,
  Modal,
  Tag,
} from 'antd'
import { PlusOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons'
import AdminHeader from '../../CommonComponents/AdminHeader'
import AdminFooter from '../../CommonComponents/AdminFooter'
import {
  getMenu,
  getPageByMenu,
  saveRoleAllocation,
  updateRoleAllocation,
  getRoleAllocationList,
} from '../../APIComponents/AdminApi'
import axiosInstance from '../../../Utils/axiosInstance'
import { toast } from 'react-toastify'

const RoleAllocation = () => {
  //============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Role Allocation')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1

  //============================================

  const [form] = Form.useForm()

  const [roleName, setRoleName] = useState('')
  const [tableData, setTableData] = useState([])
  const [roleList, setRoleList] = useState([])

  const [showForm, setShowForm] = useState(false)
  const [editingRole, setEditingRole] = useState(null)

  const [loading, setLoading] = useState(false)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [selectedRole, setSelectedRole] = useState(null)

  const handleView = (record) => {
    setSelectedRole(record)
    setViewModalVisible(true)
  }

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 5,
  })

  // ==============================
  // Fetch Role List
  // ==============================
  const fetchRoleList = async () => {
    try {
      const res = await axiosInstance.get(getRoleAllocationList)
      setRoleList(res.data)
    } catch {
      toast.error('Failed to fetch roles')
    }
  }

  // ==============================
  // Fetch Menus With Pages
  // Only menus that have submenus
  // ==============================
  const fetchMenusWithPages = async () => {
    try {
      setLoading(true)

      const menuRes = await axiosInstance.get(getMenu)

      const menusWithPages = await Promise.all(
        menuRes.data.map(async (menu) => {
          const pageRes = await axiosInstance.get(`${getPageByMenu}/${menu.id}`)

          // Skip menus without submenus
          if (!pageRes.data || pageRes.data.length === 0) {
            return null
          }

          return {
            key: `menu-${menu.id}`,
            menu_id: menu.id,
            menu_name: menu.menu_name,
            pages: pageRes.data.map((page) => ({
              key: `page-${page.id}`,
              page_id: page.id,
              page_name: page.page_name,
              is_view: false,
              is_new: false,
              is_edit: false,
              is_delete: false,
            })),
          }
        }),
      )

      const filteredMenus = menusWithPages.filter(Boolean)

      setTableData(filteredMenus)
      return filteredMenus
    } catch {
      toast.error('Failed to fetch menus')
      return []
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRoleList()
  }, [])

  // ==============================
  // Checkbox Handler (Submenu Only)
  // ==============================
  const handleCheckboxChange = (menuId, pageId, type, checked) => {
    const updated = tableData.map((menu) => {
      if (menu.menu_id === menuId) {
        return {
          ...menu,
          pages: menu.pages.map((page) =>
            page.page_id === pageId ? { ...page, [type]: checked } : page,
          ),
        }
      }
      return menu
    })

    setTableData(updated)
  }

  // ==============================
  // Submit
  // ==============================
  const onFinish = async () => {
    if (!roleName) {
      toast.error('Please enter role name')
      return
    }

    const payload = {
      ...(editingRole && { role_id: editingRole }), // add role_id only in update
      role_name: roleName,
      menus: tableData
        .map((menu) => {
          const pages = menu.pages
            .filter(
              (page) =>
                page.is_view || page.is_new || page.is_edit || page.is_delete,
            )
            .map((page) => ({
              page_id: page.page_id,
              is_view: page.is_view ? 1 : 0,
              is_new: page.is_new ? 1 : 0,
              is_edit: page.is_edit ? 1 : 0,
              is_delete: page.is_delete ? 1 : 0,
            }))

          if (pages.length > 0) {
            return {
              menu_id: menu.menu_id,
              pages,
            }
          }

          return null
        })
        .filter(Boolean),
    }

    try {
      setSubmitLoading(true)

      if (editingRole) {
        const res = await axiosInstance.put(updateRoleAllocation, payload)

        if (res.data === 'UPDATED_SUCCESS') {
          toast.success('Updated Successfully')
          setShowForm(false)
          setEditingRole(null)
          setRoleName('')
          form.resetFields()
          fetchRoleList()
        } else if (res.data === 'ALREADYEXISTROLE') {
          toast.warn('Role already exists!')
        } else {
          toast.error('Error Occurred!')
        }
      } else {
        const res = await axiosInstance.post(saveRoleAllocation, payload)

        if (res.data === 'ASSIGNED_SUCCESS') {
          toast.success('Saved Successfully')
          setShowForm(false)
          setEditingRole(null)
          setRoleName('')
          form.resetFields()
          fetchRoleList()
        } else if (res.data === 'ALREADYEXISTROLE') {
          toast.warn('Role already exists!')
        } else {
          toast.error('Error Occurred!')
        }
      }
    } catch {
      toast.error('Operation Failed')
    } finally {
      setSubmitLoading(false)
    }
  }

  // ==============================
  // Edit
  // ==============================
  const handleEdit = async (record) => {
    setShowForm(true)
    setEditingRole(record.role_id)
    setRoleName(record.role_name)

    const menusWithPages = await fetchMenusWithPages()

    const updatedMenus = menusWithPages.map((menu) => {
      const roleMenu = record.menus.find((m) => m.menu_id === menu.menu_id)

      if (!roleMenu) return menu

      return {
        ...menu,
        pages: menu.pages.map((page) => {
          const rolePage = roleMenu.pages.find(
            (p) => p.page_id === page.page_id,
          )

          if (!rolePage) return page

          return {
            ...page,
            is_view: rolePage.is_view === 1,
            is_new: rolePage.is_new === 1,
            is_edit: rolePage.is_edit === 1,
            is_delete: rolePage.is_delete === 1,
          }
        }),
      }
    })

    setTableData(updatedMenus)
  }

  // ==============================
  // Main Menu Columns
  // ==============================
  const columns = [
    {
      title: 'Menu Name',
      dataIndex: 'menu_name',
      key: 'menu_name',
    },
  ]

  // ==============================

  const handleColumnCheckAll = (menuId, type, checked) => {
    const updated = tableData.map((menu) => {
      if (menu.menu_id === menuId) {
        return {
          ...menu,
          pages: menu.pages.map((page) => ({
            ...page,
            [type]: checked,
          })),
        }
      }
      return menu
    })

    setTableData(updated)
  }

  // Expanded Submenu Table
  // ==============================
  const expandedRowRender = (menu) => {
    const menuName = menu.menu_name?.toLowerCase()

    const isReport = menuName === 'report'
    const isAdmin = menuName === 'admin'

    const pageColumns = [{ title: 'Submenu Name', dataIndex: 'page_name' }]

    //  VIEW (Show for ALL menus)
    pageColumns.push({
      title: (
        <Checkbox
          className="dark-checkbox"
          onChange={(e) =>
            handleColumnCheckAll(menu.menu_id, 'is_view', e.target.checked)
          }
        >
          View
        </Checkbox>
      ),
      align: 'center',
      render: (_, record) => (
        <Checkbox
          className="dark-checkbox"
          checked={record.is_view}
          onChange={(e) =>
            handleCheckboxChange(
              menu.menu_id,
              record.page_id,
              'is_view',
              e.target.checked,
            )
          }
        />
      ),
    })

    //  NEW (Hide only for Report)
    if (!isReport) {
      pageColumns.push({
        title: (
          <Checkbox
            className="dark-checkbox"
            onChange={(e) =>
              handleColumnCheckAll(menu.menu_id, 'is_new', e.target.checked)
            }
          >
            New
          </Checkbox>
        ),
        align: 'center',
        render: (_, record) => (
          <Checkbox
            className="dark-checkbox"
            checked={record.is_new}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_new',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    //  EDIT (Hide only for Report)
    if (!isReport) {
      pageColumns.push({
        title: (
          <Checkbox
            className="dark-checkbox"
            onChange={(e) =>
              handleColumnCheckAll(menu.menu_id, 'is_edit', e.target.checked)
            }
          >
            Edit
          </Checkbox>
        ),
        align: 'center',
        render: (_, record) => (
          <Checkbox
            className="dark-checkbox"
            checked={record.is_edit}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_edit',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    //  DELETE (Hide for Report & Admin)
    if (!isReport && !isAdmin) {
      pageColumns.push({
        title: (
          <Checkbox
            className="dark-checkbox"
            onChange={(e) =>
              handleColumnCheckAll(menu.menu_id, 'is_delete', e.target.checked)
            }
          >
            Delete
          </Checkbox>
        ),
        align: 'center',
        render: (_, record) => (
          <Checkbox
            className="dark-checkbox"
            checked={record.is_delete}
            onChange={(e) =>
              handleCheckboxChange(
                menu.menu_id,
                record.page_id,
                'is_delete',
                e.target.checked,
              )
            }
          />
        ),
      })
    }

    return (
      <Table
        columns={pageColumns}
        dataSource={menu.pages}
        pagination={false}
        rowKey="page_id"
        bordered
      />
    )
  }

  // ==============================
  // Role List Columns
  // ==============================
  const roleColumns = [
    {
      title: 'Sr. No',
      key: 'index',
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
      width: '10%',
    },
    { title: 'Role Name', dataIndex: 'role_name' },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div
        className="student-form-section container-fluid"
        style={{ minHeight: '100vh' }}
      >
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Role Allocation</h3>
          </div>
          <div className="row student-form-container shadow p-4 rounded bg-white">
            {!showForm && (
              <>
                <Row className="mb-3">
                  <Col xs={24} className="text-end">
                    <>
                      {pagePermission.is_new == 1 && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={async () => {
                            setShowForm(true)
                            setEditingRole(null)
                            setRoleName('')
                            await fetchMenusWithPages()
                          }}
                        >
                          Add
                        </Button>
                      )}
                    </>
                  </Col>
                </Row>

                <Table
                  columns={roleColumns}
                  dataSource={roleList}
                  rowKey="role_id"
                  bordered
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: roleList.length,
                    onChange: (page, pageSize) => {
                      setPagination({
                        current: page,
                        pageSize: pageSize,
                      })
                    },
                  }}
                  className="shadow rounded custom-table p-0"
                  style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
                />
              </>
            )}

            {showForm && (
              <Form form={form} onFinish={onFinish}>
                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Role Name" required>
                      <Input
                        value={roleName}
                        disabled={editingRole}
                        onChange={(e) => setRoleName(e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Table
                  columns={columns}
                  dataSource={tableData}
                  expandable={{
                    expandedRowRender,
                    rowExpandable: (record) => record.pages.length > 0,
                  }}
                  pagination={false}
                  rowKey="menu_id"
                  bordered
                  className="shadow rounded custom-table p-0"
                  style={{ overflowX: 'auto', border: '1px solid #dfdfdf' }}
                />

                <Row className="mt-4">
                  <Col xs={24} className="text-end">
                    <Space>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={submitLoading}
                      >
                        {editingRole ? 'Update' : 'Submit'}
                      </Button>
                      <Button onClick={() => setShowForm(false)}>Cancel</Button>
                    </Space>
                  </Col>
                </Row>
              </Form>
            )}
          </div>

          {/* ======== View Modal ====== */}
          <Modal style={{margin:"45px 0px"}}
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Role Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={800}
            centered
          >
            {selectedRole && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px',
                  borderRadius: '8px',
                }}
              >
                {/* Role Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Role Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div
                      style={{
                        background: '#fff',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                      }}
                    >
                      {selectedRole.role_name}
                    </div>
                  </Col>
                </Row>

                {/* Permissions Table */}
                <Table
                  dataSource={selectedRole.menus || []}
                  rowKey="menu_id"
                  pagination={false}
                  bordered
                  size="small"
                  columns={[
                    {
                      title: 'Menu',
                      dataIndex: 'menu_name',
                    },
                    {
                      title: 'Pages & Permissions',
                      render: (_, menu) => (
                        <div>
                          {menu.pages.map((page) => (
                            <div
                              key={page.page_id}
                              style={{
                                marginBottom: '8px',
                                padding: '8px',
                                background: '#ffffff',
                                borderRadius: '6px',
                                border: '1px solid #e6e6e6',
                              }}
                            >
                              <strong>{page.page_name}</strong>
                              {/* <div style={{ marginTop: '4px' }}>
                                {page.is_view === 1 && (
                                  <span style={{ marginRight: 10 }}>
                                    ✔ View
                                  </span>
                                )}
                                {page.is_new === 1 && (
                                  <span style={{ marginRight: 10 }}>✔ New</span>
                                )}
                                {page.is_edit === 1 && (
                                  <span style={{ marginRight: 10 }}>
                                    ✔ Edit
                                  </span>
                                )}
                                {page.is_delete === 1 && <span>✔ Delete</span>}
                              </div> */}
                              <div style={{ marginTop: '6px' }}>
                                {page.is_view === 1 && (
                                  <Tag color="blue" style={{ marginBottom: 4 }}>
                                    View
                                  </Tag>
                                )}

                                {page.is_new === 1 && (
                                  <Tag color="green" style={{ marginBottom: 4 }}>
                                    New
                                  </Tag>
                                )}

                                {page.is_edit === 1 && (
                                  <Tag color="orange" style={{ marginBottom: 4 }}>
                                    Edit
                                  </Tag>
                                )}

                                {page.is_delete === 1 && (
                                  <Tag color="red" style={{ marginBottom: 4 }}>
                                    Delete
                                  </Tag>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default RoleAllocation
